import { createContext, FunctionComponent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ApplicationUserDto } from '../../api/api.generated';
import { useGlobalLoading } from '../../contexts/GlobalLoadingProvider';
import { useCountries } from '../../hooks/useCountries';
import { setAnalyticsUser } from '../../utils/googleAnalytics';
import { AuthContextType } from './AuthContextType';
import { AuthService } from './AuthService';

/**
 * @implements AuthContextType
 */
const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Use the auth context. Wrapper to throw an error instead of forcing checks everywhere.
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === null) {
        throw new Error('useAuthentication must be used within a AuthProvider');
    }

    return context;
};

/**
 * Wrapper provider for the auth context.
 */
export const AuthProvider: FunctionComponent = ({ children }) => {
    const location = useLocation();
    const { currentCountry } = useCountries();
    const { incrementLoader, decrementLoader } = useGlobalLoading();

    const [isLoadingUser, setIsLoadingUser] = useState<boolean>(true);
    const service = useRef<AuthService | null>(null);
    const isAuthenticated = useRef<boolean>(false);

    /**
     * @implements AuthContextType.user
     * @implements AuthContextType.setUser
     */
    const [user, setUser] = useState<ApplicationUserDto | null>(null);

    /**
     * Listens for changes in the auth service.
     */
    const userChangeListener = useCallback((applicationUser: ApplicationUserDto | null) => {
        setAnalyticsUser(applicationUser);
        setUser(applicationUser);
    }, []);

    /**
     * Setup and configure the auth service. We need to use an effect as we need to clean up the subscriptions and
     * timers in the service using the destroy method when the component is unmounted.
     */
    useEffect(() => {
        incrementLoader();

        const authService = new AuthService();
        authService.changeListeners.add(userChangeListener);

        authService
            .initialize()
            .then(userChangeListener)
            .finally(() => {
                setIsLoadingUser(false);
                decrementLoader();
            });

        service.current = authService;

        return () => {
            setIsLoadingUser(true);

            authService.changeListeners.delete(userChangeListener);
            authService.destroy();

            service.current = null;
        };
    }, [incrementLoader, decrementLoader, userChangeListener]);

    /**
     * @implements AuthContextType.signIn
     */
    const signIn = useCallback(
        (continuePath?: string) => {
            continuePath ??= location.pathname + location.search;
            service.current?.openLogin(currentCountry.urlOnboarding, continuePath);
        },
        [currentCountry, location],
    );

    /**
     * @implements AuthContextType.signOut
     */
    const signOut = useCallback(async () => {
        await service.current?.logout();
    }, []);

    /**
     * Effect to check if the user was just signed out. If so, redirect to the onboarding login page.
     */
    useEffect(() => {
        if (isAuthenticated.current && !user) {
            signIn();
        }

        isAuthenticated.current = user !== null;
    }, [user, signIn]);

    return (
        <AuthContext.Provider
            value={{
                signIn,
                signOut,
                setUser,
                user,
            }}
        >
            {!isLoadingUser && children}
        </AuthContext.Provider>
    );
};
